import { el, toast } from './dom';
import { shopApi, SHROOM_TO_COIN_RATE, type Wallet } from './game-api';
import { ApiError } from '../core/network/api';
import { playSfx } from './audio/audio';

const COIN_ICON = '/sprites/ui/currency/coin.png';
const SHROOM_ICON = '/sprites/ui/currency/shroom.png';

const icon = (src: string, h = 18) =>
  el('img', 'shrink-0 inline-block align-middle', { src, alt: '', style: `height:${h}px;width:auto;image-rendering:pixelated` });

/**
 * Shrooms → Coins exchange. Opened from the HUD coin chip (and reusable from the
 * shop). Burns premium Shrooms for soft Coins at SHROOM_TO_COIN_RATE.
 * `onDone` refreshes the HUD chips after a successful conversion.
 */
export function openExchangeModal(onDone: () => void | Promise<void>) {
  let overlay: HTMLElement | null = null;
  const close = () => { if (overlay) playSfx('ui_close'); overlay?.remove(); overlay = null; document.removeEventListener('keydown', onKey); };
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };

  let wallet: Wallet | null = null;
  let amount = 0;

  const shroomBal = el('span', 'font-mono text-shroom', {}, '…');
  const coinBal = el('span', 'font-mono text-coin', {}, '…');
  const input = el('input',
    'w-full bg-surface-2 border border-border rounded px-3 py-2 font-mono text-lg text-center outline-none focus:border-gold',
    { type: 'number', min: '1', step: '1', value: '', placeholder: '0', inputMode: 'numeric', pattern: '[0-9]*' }) as HTMLInputElement;
  const payout = el('span', 'font-mono text-coin text-lg', {}, '0');
  const convertBtn = el('button',
    'w-full px-3 py-2 rounded bg-shroom/20 border border-shroom text-shroom hover:bg-shroom/30 disabled:opacity-40 disabled:cursor-not-allowed',
    {}, 'Convert') as HTMLButtonElement;

  const clampAmount = (v: number) => Math.max(0, Math.min(Math.floor(v || 0), wallet?.shrooms ?? 0));
  const sync = () => {
    payout.textContent = (amount * SHROOM_TO_COIN_RATE).toLocaleString();
    convertBtn.disabled = amount < 1 || !wallet || amount > wallet.shrooms;
  };
  input.addEventListener('input', () => {
    amount = clampAmount(Number(input.value));
    if (String(amount) !== input.value && input.value !== '') input.value = String(amount);
    sync();
  });

  const maxBtn = el('button', 'px-2.5 py-1 rounded border border-border text-xs text-text-dim hover:text-text hover:border-gold',
    { onClick: () => { amount = wallet?.shrooms ?? 0; input.value = String(amount); sync(); } }, 'Max');

  convertBtn.addEventListener('click', async () => {
    if (convertBtn.disabled) return;
    convertBtn.disabled = true; convertBtn.textContent = 'Converting…';
    try {
      wallet = await shopApi.exchange(amount);
      playSfx('coin_purchase');
      toast(`Converted ${amount.toLocaleString()} Shrooms → ${(amount * SHROOM_TO_COIN_RATE).toLocaleString()} Coins`, 'ok');
      await onDone();
      close();
    } catch (err) {
      toast(err instanceof ApiError ? err.message : 'Exchange failed', 'error');
      convertBtn.textContent = 'Convert'; sync();
    }
  });

  const card = el('div',
    'bg-surface border border-border rounded-lg p-5 w-[340px] max-w-[90vw] shadow-lift sheet-card',
    {},
    el('div', 'flex items-center justify-between mb-1', {},
      el('h3', 'font-heading text-gold text-lg', {}, 'Exchange'),
      el('button', 'tb-close-x text-text-dim hover:text-text text-xl leading-none', { onClick: close }, '×')),
    el('p', 'text-xs text-text-dim mb-4', {},
      `Trade Shrooms for Coins · 1 `, icon(SHROOM_ICON, 14), ` = ${SHROOM_TO_COIN_RATE} `, icon(COIN_ICON, 14)),
    el('div', 'flex justify-between text-sm mb-3', {},
      el('span', 'text-text-dim', {}, 'Your balance'),
      el('span', 'flex items-center gap-3', {}, el('span', 'flex items-center gap-1', {}, icon(SHROOM_ICON, 16), shroomBal), el('span', 'flex items-center gap-1', {}, icon(COIN_ICON, 16), coinBal))),
    el('label', 'block text-xs text-text-dim mb-1', {}, 'Shrooms to convert'),
    el('div', 'flex items-center gap-2 mb-3', {}, input, maxBtn),
    el('div', 'flex items-center justify-center gap-2 mb-4 text-text-dim', {},
      el('span', 'flex items-center gap-1', {}, icon(SHROOM_ICON, 16), el('span', 'font-mono', {}, '→')),
      el('span', 'flex items-center gap-1.5', {}, icon(COIN_ICON, 18), payout)),
    convertBtn);

  overlay = el('div', 'fixed inset-0 z-50 flex items-center justify-center bg-black/60',
    { onClick: (e) => { if (e.target === overlay) close(); } }, card);
  playSfx('ui_open');
  document.body.appendChild(overlay);
  document.addEventListener('keydown', onKey);

  // Load balances. A failure here left the modal open with two dashes and Convert
  // permanently disabled (sync() was never called and `wallet` stayed null) — no
  // reason given, no way to retry. Now it says what happened and offers a reload.
  const loadError = el('div', 'hidden mt-3 text-[11px] text-error text-center flex items-center justify-center gap-2');
  card.append(loadError);

  function loadWallet() {
    loadError.classList.add('hidden');
    void shopApi.wallet().then((w) => {
      wallet = w;
      shroomBal.textContent = w.shrooms.toLocaleString();
      coinBal.textContent = w.coins.toLocaleString();
      sync();
    }).catch(() => {
      wallet = null;
      shroomBal.textContent = '—'; coinBal.textContent = '—';
      sync();                       // keeps Convert disabled, but by a rule, not by omission
      loadError.replaceChildren("Couldn't load your balance.",
        el('button', 'underline hover:text-text', { onClick: () => loadWallet() }, 'Try again'));
      loadError.classList.remove('hidden');
    });
  }
  loadWallet();
}
